// src/components/LoginPortal.js
import React from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Fade,
  Stack,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Engineering as EngineerIcon,
  Business as CustomerIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/humbertack-workflow.firebasestorage.app/o/Minimalist%20HumberTrack%20Logo%20Design.png?alt=media&token=7fac08dd-27a3-466a-8172-48d50a827959";

function LoginPortal({ onSelectPortal }) {
  const userTypes = [
    {
      id: 'admin',
      title: 'Admin',
      description: 'Manage jobs, engineers, clients & invoices',
      icon: <AdminIcon sx={{ fontSize: 48 }} />,
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    },
    {
      id: 'engineer',
      title: 'Engineer',
      description: 'View & complete your assigned jobs',
      icon: <EngineerIcon sx={{ fontSize: 48 }} />,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
    },
    {
      id: 'customer',
      title: 'Customer',
      description: 'Track jobs, view invoices & request services',
      icon: <CustomerIcon sx={{ fontSize: 48 }} />,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top, #1d4ed8 0%, #020617 45%, #020617 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background:
            'radial-gradient(circle at 20% 50%, #0ea5e9 0%, transparent 50%), radial-gradient(circle at 80% 80%, #4f46e5 0%, transparent 50%)',
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.1 },
            '50%': { opacity: 0.2 },
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={800}>
          <Box>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box
                component="img"
                src={LOGO_URL}
                alt="HumberTrack"
                sx={{
                  width: 100,
                  height: 100,
                  objectFit: 'contain',
                  mb: 3,
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-10px)' },
                  },
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  background: 'linear-gradient(90deg, #0ea5e9 0%, #4f46e5 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                HumberTrack
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Workflow Management System
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Choose your login portal
              </Typography>
            </Box>

            {/* Login Type Cards */}
            <Grid container spacing={3} justifyContent="center">
              {userTypes.map((type, index) => (
                <Grid item xs={12} sm={6} md={4} key={type.id}>
                  <Fade in timeout={1000 + index * 200}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(145deg, rgba(15,23,42,0.95), rgba(15,23,42,0.9))',
                        border: `1px solid rgba(148,163,184,0.2)`,
                        borderRadius: 3,
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: `0 20px 40px ${type.color}40`,
                          borderColor: type.color,
                        },
                      }}
                      onClick={() => onSelectPortal(type.id)}
                    >
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 3,
                            background: type.gradient,
                            boxShadow: `0 8px 24px ${type.color}40`,
                          }}
                        >
                          {type.icon}
                        </Avatar>

                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            mb: 2,
                            color: type.color,
                          }}
                        >
                          {type.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3, minHeight: 48 }}
                        >
                          {type.description}
                        </Typography>

                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowIcon />}
                          sx={{
                            background: type.gradient,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: `0 4px 12px ${type.color}40`,
                            '&:hover': {
                              boxShadow: `0 6px 20px ${type.color}60`,
                            },
                          }}
                        >
                          Sign in as {type.title}
                        </Button>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  HumberTrack • Vehicle Technology Installations
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Need help? Contact your system administrator
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
                  © 2025 HumberTrack Ltd. All rights reserved.
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}

export default LoginPortal;